import { Box, Typography as ChronoUiTypography, IconButton, styled, Tooltip } from '@chronosphereio/chrono-ui';
import { Fragment, memo, useMemo } from 'react';
import {
  AppMetadataProps,
  formatValue,
  SeriesConfig,
  SeriesMenuItemOptions,
  TooltipContextMenuCtx,
  UnitOptions,
} from '@/core';
import { SeriesMarker, type SeriesMarkerVariant } from '../PanelTooltip';
import { useSeriesActionContext } from './SeriesActionProvider';
import { SeriesTooltipActions } from './SeriesTooltipActions';
import {
  EMPHASIZED_SERIES_DESCRIPTION,
  NEARBY_SERIES_DESCRIPTION,
  SeriesInfoContext,
  TOOLTIP_LABELS_MAX_WIDTH,
  TooltipConfig,
  TooltipLink,
} from './tooltip-model';
import { checkUnitPrecision } from './utils';

export type SeriesInfoProps = {
  links?: TooltipLink[];
  isTooltipPinned?: boolean;
  config?: SeriesConfig;
  unit?: UnitOptions;
  enableTooltipPreciseValues?: boolean;
  markerType?: SeriesMarkerVariant;
  tooltipContextMenuProps?: TooltipConfig['tooltipContextMenuProps'];
  tooltipContextMenuData?: TooltipConfig['tooltipContextMenuData'];
  appMetadata?: AppMetadataProps;
  clearPinned?: () => void;
} & SeriesInfoContext;

export const SeriesInfo = memo(function SeriesInfo(props: SeriesInfoProps) {
  const {
    seriesId,
    seriesName,
    formattedY,
    markerColor,
    markerType = 'solid',
    emphasizeText = false,
    wrapSeriesName,
    isTooltipPinned,
    config,
    x: timestamp,
    y,
    unit,
    enableTooltipPreciseValues,
    tooltipContextMenuProps,
    tooltipContextMenuData,
    appMetadata,
  } = props;

  const renderSeriesAction = useSeriesActionContext()?.renderSeriesAction;
  const getSeriesById = useSeriesActionContext()?.getSeriesById;

  const seriesMenuOptions = useMemo(() => {
    const seriesMenuOptions: SeriesMenuItemOptions[] = [];
    const ctx: TooltipContextMenuCtx = {
      timestamp,
      appMetadata,
      isTooltipPinned: !!isTooltipPinned,
      seriesKey: seriesId,
    };
    const seriesWithMapping = getSeriesById?.(seriesId);
    if (seriesWithMapping) {
      // Remove implementation details from series
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { queryResultsIndex, mappingIndex, ...series } = seriesWithMapping;
      if (tooltipContextMenuProps?.seriesMenuItems) {
        const seriesMenuItems = tooltipContextMenuProps?.seriesMenuItems(ctx, series);
        seriesMenuItems.forEach((item) => {
          seriesMenuOptions.push(item);
        });
      }

      if (tooltipContextMenuData) {
        const tooltipContextMenu = tooltipContextMenuData[queryResultsIndex];
        if (tooltipContextMenu) {
          const seriesMenuItems = tooltipContextMenu(ctx, series);
          seriesMenuItems.forEach((item) => {
            seriesMenuOptions.push(item);
          });
        }
      }
    }

    return seriesMenuOptions;
  }, [
    timestamp,
    isTooltipPinned,
    getSeriesById,
    seriesId,
    appMetadata,
    tooltipContextMenuData,
    tooltipContextMenuProps,
  ]);

  const hasLinks = props.links && props.links.length > 0;

  // Get formatted y value.
  let yValueStr = String(y);
  if (formattedY !== undefined) {
    // Needed for legacy LineChartBase and forked TimeSeriesChart in monitors where formatter
    // is called in nearby series lookup (legacyCheckforNearbySeries).
    yValueStr = formattedY;
  } else if (config?.unit !== undefined) {
    const adjustedUnit = checkUnitPrecision(config.unit, enableTooltipPreciseValues);
    yValueStr = formatValue(y, adjustedUnit); // Unit from series config is used for right yAxis.
  } else if (unit !== undefined) {
    // Default unit for left yAxis, calling checkUnitPrecision is not needed since it is already called in TimeChartTooltip.
    yValueStr = formatValue(y, unit);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        maxWidth: '100%',
        alignItems: 'center',
      }}
    >
      <Box
        paddingY={0}
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <SeriesMarker color={config?.color ?? markerColor} variant={markerType} />
          <Box
            component="span"
            sx={{
              display: 'block',
              flex: '1',
              minWidth: 150,
              maxWidth: TOOLTIP_LABELS_MAX_WIDTH,
            }}
            aria-label={emphasizeText ? EMPHASIZED_SERIES_DESCRIPTION : NEARBY_SERIES_DESCRIPTION}
          >
            <SeriesInfoTypography
              variant="body2"
              title={seriesName} // Shows full series name on hover
              emphasizeText={emphasizeText}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: wrapSeriesName ? 'normal' : 'nowrap',
                // When wrapping, prefer breaking at the <wbr /> hints inserted around
                // common label syntax (`,`, `{`, `}`). `overflow-wrap: break-word` is
                // the emergency fallback for tokens that still do not fit (e.g. long UUIDs).
                ...(wrapSeriesName && {
                  overflowWrap: 'break-word',
                  wordBreak: 'normal',
                }),
              }}
            >
              {wrapSeriesName ? renderWrappableSeriesName(seriesName) : seriesName}
            </SeriesInfoTypography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          paddingLeft: 1.5,
          textAlign: 'right',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap', // Ensure long values with units do not wrap in EmphasizedSeriesSection
        }}
        aria-label="tooltip series value"
      >
        <SeriesInfoTypography variant="body2" emphasizeText={emphasizeText}>
          {yValueStr}
        </SeriesInfoTypography>
      </Box>

      {isTooltipPinned && !hasLinks && seriesMenuOptions.length === 1 && (
        <SeriesActionIconButton seriesAction={seriesMenuOptions[0]} />
      )}

      {renderSeriesAction && (
        <Box
          sx={{
            textAlign: 'right',
            paddingLeft: 1,
            verticalAlign: 'middle',
          }}
        >
          {renderSeriesAction(props?.seriesId, {
            isTooltipPinned: isTooltipPinned,
          })}
        </Box>
      )}

      {isTooltipPinned && (hasLinks || seriesMenuOptions.length > 1) && (
        <Box
          sx={{
            textAlign: 'right',
            paddingLeft: 1,
          }}
        >
          <SeriesTooltipActions
            seriesInfo={props}
            links={props.links}
            seriesActions={seriesMenuOptions}
            onClick={props.clearPinned}
          />
        </Box>
      )}
    </Box>
  );
});

// Inserts soft break opportunities (<wbr />) after common label-syntax characters
// so wrapped series names break in readable spots (after `{`, `,`, `}`) rather than
// arbitrary positions inside identifiers like `job="node"`. Does not modify the
// underlying text, so the rendered name remains exactly equal to `seriesName` for
// copy/paste and selection.
const WBR_SPLIT_REGEX = /([{},])/g;
function renderWrappableSeriesName(seriesName: string) {
  const parts = seriesName.split(WBR_SPLIT_REGEX);
  return parts.map((part, i) => (
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 && <wbr />}
    </Fragment>
  ));
}

const SeriesInfoTypography = styled(ChronoUiTypography, {
  shouldForwardProp: (prop) => prop !== 'emphasizeText',
})<{ emphasizeText?: boolean }>(({ theme, emphasizeText }) => ({
  // Use bold font weight so emphasized series in tooltip is obvious in dark mode.
  fontWeight: emphasizeText ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  lineHeight: '24px',
}));

interface SeriesActionIconButtonProps {
  seriesAction?: SeriesMenuItemOptions;
}

const SeriesActionIconButton = ({ seriesAction }: SeriesActionIconButtonProps) => {
  if (!seriesAction) return null;

  return (
    <Box
      sx={{
        textAlign: 'right',
        paddingLeft: 1,
        lineHeight: '18px', // this is needed to prevent the table row height from growing
      }}
    >
      <Tooltip Content={seriesAction.title}>
        <a href={seriesAction.href} target="_blank" rel="noopener noreferrer">
          <IconButton
            size="small"
            onClick={seriesAction.onClick}
            sx={{ padding: '1px' }}
            aria-label={seriesAction.description}
          >
            <seriesAction.icon />
          </IconButton>
        </a>
      </Tooltip>
    </Box>
  );
};
